import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTradeDetails, useTrades, type TradeDirectionFilter, type TradeStatusFilter } from "../api/hooks/useTrades";
import { useBotInstances } from "../api/hooks/useBotInstances";
import { GRID_COLS, TradeRow } from "../components/TradeRow/TradeRow";
import { TradeDetailPanel } from "../components/TradeDetailPanel/TradeDetailPanel";
import { formatPnl } from "../utils/format";

const PAGE_SIZE = 50;

const FILTERS: { key: TradeDirectionFilter | "open"; label: string }[] = [
  { key: "all", label: "Все" },
  { key: "long", label: "LONG" },
  { key: "short", label: "SHORT" },
  { key: "open", label: "Открытые" },
];

export function Trades() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filter, setFilter] = useState<TradeDirectionFilter | "open">("all");
  const [botFilter, setBotFilter] = useState<string>("all");
  const { data: botInstances } = useBotInstances();
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<string | null>(null);
  const [page, setPage] = useState(1);

  // Changing the filter/search invalidates whatever page we were on (a
  // narrower result set may not even have a page 2 anymore) — always land
  // back on page 1 rather than showing an empty page silently.
  useEffect(() => {
    setPage(1);
  }, [filter, query, botFilter]);

  // A trade clicked as a marker on the Overview chart arrives here as
  // ?highlight=<trade_id> — select it immediately (independent of the
  // current filter/page, useTradeDetails fetches it directly by ID) and
  // consume the param so it doesn't stick around after manual navigation.
  useEffect(() => {
    const highlight = searchParams.get("highlight");
    if (highlight) {
      setSelected(highlight);
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete("highlight");
        return next;
      }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const direction: TradeDirectionFilter = filter === "long" || filter === "short" ? filter : "all";
  const status: TradeStatusFilter = filter === "open" ? "open" : "all";

  const { data, isLoading } = useTrades({
    direction,
    status,
    q: query,
    botInstanceId: botFilter === "all" ? undefined : botFilter,
    page,
    page_size: PAGE_SIZE,
  });
  const trades = data?.trades ?? [];
  const activeId = selected ?? trades[0]?.trade_id ?? null;
  const { data: details } = useTradeDetails(activeId);
  const totalPages = Math.max(1, Math.ceil((data?.total ?? 0) / PAGE_SIZE));

  return (
    <div style={{ padding: "26px 28px 44px", display: "flex", flexDirection: "column", gap: 18 }}>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center" }}>
        <div style={{ display: "flex", gap: 2, padding: 2, borderRadius: 11, background: "var(--surface2)" }}>
          {FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => setFilter(f.key)}
              style={{
                padding: "6px 13px",
                border: 0,
                borderRadius: 9,
                cursor: "pointer",
                font: "600 12px 'Golos Text',sans-serif",
                background: filter === f.key ? "var(--accent)" : "transparent",
                color: filter === f.key ? "var(--onfill)" : "var(--fg2)",
              }}
            >
              {f.label}
            </button>
          ))}
        </div>
        <input
          type="text"
          placeholder="Поиск по ID сделки"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          style={{
            flex: "1 1 200px",
            maxWidth: 280,
            padding: "7px 12px",
            border: "1px solid var(--line)",
            borderRadius: 10,
            background: "var(--surface)",
            color: "var(--fg)",
            font: "400 12.5px 'Golos Text',sans-serif",
            outline: "none",
          }}
        />
        {botInstances && botInstances.instances.length > 1 && (
          <select
            value={botFilter}
            onChange={(e) => setBotFilter(e.target.value)}
            title="Фильтр по боту"
            style={{
              padding: "6px 11px",
              border: "1px solid var(--line)",
              borderRadius: 10,
              cursor: "pointer",
              font: "600 12px 'JetBrains Mono',monospace",
              background: "var(--surface2)",
              color: "var(--fg2)",
            }}
          >
            <option value="all">Все боты</option>
            {botInstances.instances.map((b) => (
              <option key={b.bot_instance_id} value={b.bot_instance_id}>
                {b.display_name ?? b.bot_instance_id}
              </option>
            ))}
          </select>
        )}
      </div>

      {data && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 0, borderRadius: 16, background: "var(--surface)", overflow: "hidden", boxShadow: "var(--shadow)" }}>
          <SummaryCell label="Сделок" value={String(data.summary.count)} />
          <SummaryCell label="Прибыльных" value={`${data.summary.win_rate_pct}%`} color="var(--long)" />
          <SummaryCell label="P&L выборки" value={formatPnl(data.summary.pnl_sum)} color={parseFloat(data.summary.pnl_sum) >= 0 ? "var(--long)" : "var(--short)"} />
          <SummaryCell
            label="Лучшая"
            value={formatPnl(data.summary.best)}
            color="var(--long)"
            onClick={data.summary.best_trade_id ? () => setSelected(data.summary.best_trade_id) : undefined}
          />
          <SummaryCell
            label="Худшая"
            value={formatPnl(data.summary.worst)}
            color="var(--short)"
            last
            onClick={data.summary.worst_trade_id ? () => setSelected(data.summary.worst_trade_id) : undefined}
          />
        </div>
      )}

      <div style={{ display: "flex", flexWrap: "wrap", gap: 18, alignItems: "flex-start" }}>
        <section style={{ flex: "0 1 756px", minWidth: 706, borderRadius: 16, background: "var(--surface)", boxShadow: "var(--shadow)", overflow: "clip" }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: GRID_COLS,
              gap: 12,
              padding: "10px 16px",
              borderBottom: "1px solid var(--line)",
              background: "var(--surface3)",
              font: "600 10.5px 'Golos Text',sans-serif",
              letterSpacing: ".06em",
              textTransform: "uppercase",
              color: "var(--fg2)",
              position: "sticky",
              top: 0,
              zIndex: 20,
            }}
          >
            <span>Сделка</span>
            <span>Открытие</span>
            <span>Цена</span>
            <span style={{ textAlign: "right" }}>Итоговый P&L</span>
            <span />
          </div>
          <div style={{ overflowX: "auto" }}>
            {isLoading && <div style={{ padding: 16, font: "400 12.5px 'Golos Text',sans-serif", color: "var(--fg3)" }}>Загрузка…</div>}
            {trades.map((t) => (
              <TradeRow key={t.trade_id} trade={t} selected={t.trade_id === activeId} onSelect={setSelected} />
            ))}
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 14,
              padding: "12px 16px",
              font: "400 11.5px 'JetBrains Mono',monospace",
              color: "var(--fg3)",
            }}
          >
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              style={{
                padding: "5px 11px",
                border: "1px solid var(--line)",
                borderRadius: 8,
                background: "transparent",
                color: page <= 1 ? "var(--fg3)" : "var(--fg2)",
                cursor: page <= 1 ? "default" : "pointer",
                opacity: page <= 1 ? 0.5 : 1,
              }}
            >
              ← назад
            </button>
            <span>
              стр. {page} из {totalPages} · показано {trades.length} из {data?.total ?? 0}
            </span>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              style={{
                padding: "5px 11px",
                border: "1px solid var(--line)",
                borderRadius: 8,
                background: "transparent",
                color: page >= totalPages ? "var(--fg3)" : "var(--fg2)",
                cursor: page >= totalPages ? "default" : "pointer",
                opacity: page >= totalPages ? 0.5 : 1,
              }}
            >
              вперёд →
            </button>
          </div>
        </section>

        <aside
          style={{
            flex: "1 1 340px",
            minWidth: 300,
            position: "sticky",
            top: 0,
            display: "flex",
            flexDirection: "column",
            padding: "18px 20px 20px",
            borderRadius: 16,
            background: "var(--surface)",
            boxShadow: "var(--shadow)",
          }}
        >
          {!details ? (
            <span style={{ font: "400 13px 'Golos Text',sans-serif", color: "var(--fg3)" }}>Выберите сделку</span>
          ) : (
            <TradeDetailPanel details={details} />
          )}
        </aside>
      </div>
    </div>
  );
}

function SummaryCell({
  label,
  value,
  color,
  last,
  onClick,
}: {
  label: string;
  value: string;
  color?: string;
  last?: boolean;
  onClick?: () => void;
}) {
  const Tag = onClick ? "button" : "div";
  return (
    <Tag
      type={onClick ? "button" : undefined}
      onClick={onClick}
      title={onClick ? "Открыть эту сделку в деталях" : undefined}
      style={{
        flex: "1 1 130px",
        padding: "12px 16px",
        border: 0,
        borderRight: last ? undefined : "1px solid var(--line)",
        background: "transparent",
        textAlign: "left",
        cursor: onClick ? "pointer" : "default",
      }}
    >
      <div style={{ font: "400 11px 'Golos Text',sans-serif", color: "var(--fg3)" }}>{label}</div>
      <div style={{ font: "700 18px 'JetBrains Mono',monospace", marginTop: 2, color: color ?? "var(--fg)" }}>{value}</div>
    </Tag>
  );
}
